import { CollectionPage, mergeCollectionPage } from '@src/components/sco/Advisors/shared/mergeCollectionPage';
import { useCallback, useEffect, useMemo, useState } from 'react';
type FetchMoreResult<TData> = {
  data?: TData | null;
};
type AdvisorsCollectionFetchMore<TData> = (options: {
  variables: {
    page: number;
  };
}) => Promise<FetchMoreResult<TData>>;
type UseAdvisorsCollectionLoadMoreOptions<TData, TItem> = {
  /** Pass `query.fetchMore` — extra pages are merged in React, not the Apollo cache. */
  fetchMore: unknown;
  field: keyof TData;
  firstPageItems: TItem[];
  hasNextPage?: boolean | null;
  /** Reset extra pages when filters / buckets change. */
  resetKey?: string;
};

/**
 * Offset load-more for a single root collection field.
 * Keep the watch query on `page: 1`. Later pages are fetchMore'd with
 * `no-cache` (Apollo default for fetchMore) and concatenated locally so
 * page-2 results cannot replace the first page in the cache.
 */
export function useAdvisorsCollectionLoadMore<TData, TItem = unknown>({
  fetchMore,
  field,
  firstPageItems,
  hasNextPage,
  resetKey
}: UseAdvisorsCollectionLoadMoreOptions<TData, TItem>) {
  const [page, setPage] = useState(1);
  const [extraItems, setExtraItems] = useState<TItem[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(!!hasNextPage);
  useEffect(() => {
    setPage(1);
    setExtraItems([]);
    setHasMore(!!hasNextPage);
    // Filter/bucket changes are the reset signal — first-page rows arrive on the next query result.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey is the intentional gate
  }, [resetKey]);
  useEffect(() => {
    if (page === 1) {
      setHasMore(!!hasNextPage);
    }
  }, [hasNextPage, page]);
  const items = useMemo(() => {
    const merged = mergeCollectionPage({
      items: firstPageItems
    }, {
      items: extraItems
    });
    return (merged?.items ?? firstPageItems) as TItem[];
  }, [extraItems, firstPageItems]);
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) {
      return;
    }
    const nextPage = page + 1;
    setLoadingMore(true);
    const fetchMorePage = fetchMore as AdvisorsCollectionFetchMore<TData>;
    try {
      const result = await fetchMorePage({
        variables: {
          page: nextPage
        }
      });
      const incoming = result.data?.[field] as CollectionPage<TItem> | null | undefined;
      const nextItems = (incoming?.items ?? []).filter((item): item is TItem => item != null);
      setExtraItems(current => {
        const merged = mergeCollectionPage({
          items: current
        }, {
          items: nextItems
        });
        return (merged?.items ?? current) as TItem[];
      });
      setHasMore(!!incoming?.pagination?.hasNextPage);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, field, hasMore, loadingMore, page]);
  const resetPage = useCallback(() => {
    setPage(1);
    setExtraItems([]);
  }, []);
  return {
    items,
    loadMore,
    loadingMore,
    page,
    resetPage,
    hasNextPage: hasMore
  };
}